import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Text from "~/components/atoms/Text";
import { Card, CardContent } from "~/components/ui/card";
import { Carousel, CarouselApi, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "~/components/ui/carousel";
import { Button } from "~/components/ui/button";
import PGridCard from "../Card/PGridCard";
import { Drawer, DrawerContent } from "~/components/ui/drawer";
import { buildingSlug, listingSlug, propertySlug, workspaceSlug } from "~/lib/helpers/slugHelper";
import { getSpaceTypeCountsFromListing } from "~/lib/helpers/spaceTypeChipHelper";
import {
  buildCardListingDisplayFromListing,
  resolveListingIsFeatured,
  type CardListingLike,
} from "~/lib/helpers/cardListingDisplay";

const MAP_CARD_ZOOM = "80%";

function buildMapCardLinks(b: CardListingLike, isPropertyCard?: boolean) {
  const link = isPropertyCard ? propertySlug(b as any) : listingSlug(b as any);
  const propertyLink = propertySlug({
    ...b,
    id: isPropertyCard ? b.id : b.propertyId,
  } as any);
  const buildingLink = b?.buildingData ? buildingSlug(b.buildingData as any) : "";
  const workspaceLink = b?.workspaceData ? workspaceSlug(b.workspaceData as any) : "";

  return { link, propertyLink, buildingLink, workspaceLink };
}

export default function MapCard({
  currentMarkers,
  onClose,
  mobile,
  isPropertyCard,
  featuredPropertyIds,
}: {
  currentMarkers: any[];
  onClose: Function;
  mobile: boolean;
  isPropertyCard?: boolean;
  featuredPropertyIds?: ReadonlySet<string>;
}) {
  const [api, setApi] = useState<CarouselApi>();
  const [currentIndex, setCurrentIndex] = useState(0);

  const prevRef = useRef<HTMLButtonElement | null>(null);
  const nextRef = useRef<HTMLButtonElement | null>(null);

  // Listen to slide changes (works for both swipes AND button clicks)
  useEffect(() => {
    if (!api) return;

    const updateIndex = () => {
      setCurrentIndex(api.selectedScrollSnap());
    };

    updateIndex();
    api.on("select", updateIndex);

    return () => {
      api.off("select", updateIndex);
    };
  }, [api]);

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : currentMarkers.length - 1));
    prevRef.current?.click();
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < currentMarkers.length - 1 ? prev + 1 : 0));
    nextRef.current?.click();
  };

  const renderMarkerCard = (
    currentMarker: any,
    isMapCard: boolean,
  ) => {
    const b = currentMarker.data as CardListingLike;
    const { link, propertyLink, buildingLink, workspaceLink } = buildMapCardLinks(
      b,
      isPropertyCard,
    );
    const display = buildCardListingDisplayFromListing(b, {
      isProperty: isPropertyCard,
    });

    return (
      <PGridCard
        {...display}
        loading={false}
        link={link}
        propertyLink={propertyLink}
        isFirstVisible={false}
        isPropertyPage={!!isPropertyCard}
        buildingLink={buildingLink}
        workspaceLink={workspaceLink}
        altText={b.addressFormula}
        titleText={b.buildingName || b.name}
        isFeatured={resolveListingIsFeatured(b, featuredPropertyIds)}
        hideTransportInfo
        isMapCard={isMapCard}
        showFavoriteButton={isPropertyCard ? false : true}
        showSpaceTypeChips={mobile}
        spaceTypeLabel={
          isPropertyCard
            ? b.propertyType__c || ""
            : (b.spaceTypeFormula && b.spaceTypeFormula.toUpperCase()) ||
            "Private Office"
        }
        spaceTypeCounts={getSpaceTypeCountsFromListing(b)}
        cardClassName="w-full h-full rounded-2xl overflow-hidden border-0 shadow-none"
      />
    );
  };

  const markerNavigation =
    currentMarkers.length > 1 ? (
      <div className='flex justify-between items-center p-3 bg-white'>
        <Button variant={'ghost'} onClick={handlePrev} disabled={currentIndex + 1 === 1} className='p-2 rounded-full'>
          <ChevronLeft size={24} />
        </Button>

        <Text className="text-sm font-medium">
          {`Showing ${currentIndex + 1} of ${currentMarkers.length}`}
        </Text>

        <Button
          variant="ghost"
          onClick={handleNext}
          disabled={
            currentIndex + 1 === currentMarkers.length
          }
          className="p-2 rounded-full"
        >
          <ChevronRight size={24} />
        </Button>
      </div>
    ) : null;

  const cardsContent = (isDrawer: boolean) =>
    currentMarkers.length === 1 ? (
      renderMarkerCard(currentMarkers[0], !isDrawer)
    ) : (
      <div className="w-full flex flex-col overflow-hidden rounded-2xl border-0 bg-white border-gray-100 shadow-2xl">
        <Carousel
          setApi={setApi}
          opts={{
            ...(mobile ? { loop: true } : {}),
            watchDrag: (_emblaApi, event) => {
              const target = event.target;

              if (
                target instanceof Element &&
                target.closest("[data-card-image-carousel]")
              ) {
                return false;
              }

              return true;
            },
          }}
          className="w-full"
        >
          <CarouselContent className="ml-0">
            {currentMarkers.map((currentMarker: any) => (
              <CarouselItem
                key={
                  currentMarker.id ??
                  currentMarker.data?.id
                }
                className="pl-0"
              >
                {renderMarkerCard(
                  currentMarker,
                  !isDrawer,
                )}
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="absolute left-2 top-1/2 transform -translate-y-1/2 hidden" ref={prevRef} />
          <CarouselNext className="absolute right-2 top-1/2 transform -translate-y-1/2 hidden" ref={nextRef} />
        </Carousel>
        {markerNavigation}
      </div>
    );

  return (
    <>
      {mobile ? (
        <div className="flex justify-center w-full">
          <div className="flex justify-center w-full">
            <Drawer open={!!currentMarkers.length} onOpenChange={(val) => onClose(val)}>
              <DrawerContent
                data-map-drawer
                className="bottom-4 inset-x-4 mx-auto max-w-md bg-transparent border-0 p-0 shadow-2xl outline-none"
              >
                <div
                  data-vaul-no-drag
                  className="w-full overflow-hidden rounded-lg"
                  style={{ zoom: MAP_CARD_ZOOM }}
                >
                  {cardsContent(true)}
                </div>
              </DrawerContent>
            </Drawer>
          </div>
        </div>
      ) : (
        <div
          className="mx-auto w-[340px] max-w-sm"
          style={{ zoom: MAP_CARD_ZOOM }}
        >
          {cardsContent(false)}
        </div>
      )}
    </>
  );
}
